import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Spinner,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import OnboardingLayout from "../components/onboarding/OnboardingLayout";

export default function OnboardingWaitlist() {
  const toast = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [createName, setCreateName] = useState("Waitlist");
  const [error, setError] = useState(null);
  const [lockedField, setLockedField] = useState(null);
  const [isLocked, setIsLocked] = useState(false);

  useEffect(() => {
    fetchFields();
  }, []);

  const fetchFields = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch("/api/waitlist_custom_fields");
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to load custom fields");
      }

      if (data.not_applicable) {
        window.dispatchEvent(new Event("onboarding-progress-refresh"));
        navigate("/onboarding/waitlist/customize", { replace: true });
        return;
      }

      if (data.selected?.custom_field_configuration_id) {
        setLockedField({
          label: data.selected.custom_field_label,
          custom_field_id: data.selected.custom_field_id,
          custom_field_configuration_id: data.selected.custom_field_configuration_id,
        });
        setIsLocked(true);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const response = await fetch("/api/waitlist_custom_fields", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
        },
        body: JSON.stringify({ name: createName }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to create custom field");
      }

      const created = data.field;

      toast({
        title: "Custom field created",
        description: "Your waitlist custom field is ready.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      window.dispatchEvent(new Event("onboarding-progress-refresh"));
      setLockedField(created);
    } catch (err) {
      toast({
        title: "Error",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCreating(false);
    }
  };

  const handleConfirmLock = async () => {
    if (!lockedField) {
      toast({
        title: "Select or create a field first",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setConfirming(true);
    try {
      const response = await fetch("/api/waitlist_custom_fields/select", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
        },
        body: JSON.stringify({
          waitlist_custom_field: {
            custom_field_label: lockedField.label,
            custom_field_id: lockedField.custom_field_id,
            custom_field_configuration_id: lockedField.custom_field_configuration_id,
          },
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || data.errors?.join(", ") || "Failed to confirm field");
      }

      toast({
        title: "Waitlist field locked",
        description: "Contact support if you need to change this later.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      window.dispatchEvent(new Event("onboarding-progress-refresh"));
      setIsLocked(true);
    } catch (err) {
      toast({
        title: "Error",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <OnboardingLayout title="Create or Set Your Custom Field">
        <Box display="flex" justifyContent="center" alignItems="center" minH="300px">
          <Spinner size="xl" color="blue.500" thickness="4px" />
        </Box>
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout
      title="Create or Set Your Custom Field"
      subtitle="Choose a true/false custom field on clients or create a new one for your waitlist."
    >
      <VStack align="stretch" spacing={6}>
        {error && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={5} bg="white">
          <Heading size="sm" mb={3}>
            Create Your Waitlist Field
          </Heading>
          <Text fontSize="sm" color="gray.600" mb={4}>
            We’ll create a new true/false client field for your waitlist, then you can lock it in.
          </Text>
          <Box
            border="1px solid"
            borderColor={lockedField ? "blue.200" : "gray.200"}
            borderRadius="lg"
            p={5}
            bg={lockedField ? "blue.50" : "white"}
            opacity={isLocked ? 0.6 : 1}
          >
            <Flex justify="space-between" align="center" mb={3}>
              <Heading size="sm">Create New Field</Heading>
              {isLocked && (
                <Badge colorScheme="green" fontSize="xs" px={2} py={1}>
                  Locked
                </Badge>
              )}
            </Flex>
            <Text fontSize="sm" color="gray.600" mb={4}>
              We’ll create a new true/false client field for your waitlist.
            </Text>
            <FormControl isDisabled={isLocked}>
              <FormLabel fontSize="sm" color="gray.700">Custom field name</FormLabel>
              <Input
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="Waitlist"
                isDisabled={isLocked}
              />
            </FormControl>
            <Button
              mt={4}
              colorScheme="blue"
              variant="outline"
              onClick={handleCreate}
              isLoading={creating}
              isDisabled={isLocked}
            >
              Create
            </Button>
          </Box>
        </Box>

        <Box border="1px solid" borderColor="gray.200" borderRadius="lg" p={5} bg="white">
          <Flex justify="space-between" align="center" mb={2}>
            <Heading size="sm">Final Selection</Heading>
            {isLocked ? (
              <Badge colorScheme="green" fontSize="xs" px={2} py={1}>
                Locked
              </Badge>
            ) : lockedField ? (
              <Badge colorScheme="blue" fontSize="xs" px={2} py={1}>
                Ready to Lock
              </Badge>
            ) : (
              <Badge colorScheme="gray" fontSize="xs" px={2} py={1}>
                Not Set
              </Badge>
            )}
          </Flex>
          {lockedField ? (
            <Text fontSize="sm" color="gray.700">
              Using: <strong>{lockedField.label}</strong>
            </Text>
          ) : (
            <Text fontSize="sm" color="gray.500">
              Create your waitlist field above, then confirm to lock it in.
            </Text>
          )}
          <Button
            mt={4}
            colorScheme="blue"
            onClick={handleConfirmLock}
            isLoading={confirming}
            isDisabled={!lockedField || isLocked}
          >
            {isLocked ? "Locked" : "Confirm & Lock"}
          </Button>
          <Text mt={2} fontSize="xs" color="gray.500">
            Need to change this later? Contact support.
          </Text>
        </Box>
      </VStack>
      <Box mt={6}>
        <Button
          colorScheme="blue"
          variant="outline"
          onClick={() => navigate("/onboarding/waitlist/customize")}
          isDisabled={!isLocked}
        >
          Go to Customize Your Waitlist
        </Button>
        <Text mt={2} fontSize="xs" color="gray.500">
          You must lock your waitlist field before continuing.
        </Text>
      </Box>
    </OnboardingLayout>
  );
}
